import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSyncRef } from 'hooks';
import type { Translator } from './types';
import { langStore } from './config';

/**
 * This hook would be necessary if the user
 * had the power to change the language...
 *
 * We're using the i18n lib as a speed lane.
 *
 * Later it would make more sense to have a custom wrapper,
 * as we do not have dynamic lang changes.
 *
 */
export function useLang() {
  const { t, i18n } = useTranslation();

  const syncParams = useSyncRef({
    t,
    i18n,
  });

  const stableT = useCallback((...params: Parameters<Translator>) => {
    return syncParams.current.t(...params);
    // `t` is not stable between renders. We need it to be when referencing inside callbacks...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actions = useMemo(
    () => ({
      changeLanguage: (lang: string) => {
        syncParams.current.i18n.changeLanguage(lang);
        langStore.lang.set(lang);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const mutable = useMemo(
    () => ({
      lang: i18n.language,
    }),
    [i18n.language],
  );

  return {
    t: stableT as Translator,
    ...mutable,
    ...actions,
  };
}
